import type {Citation, CitationTowStatus} from "../types";

export type TowWorkflowStatus = CitationTowStatus;

export type TowActionMatrix = {
  showWorkflow: boolean;
  canRequestTow: boolean;
  canCompleteTow: boolean;
  canCancelTow: boolean;
  isTerminal: boolean;
};

export type TowBadgeState = {
  showWorkflow: boolean;
  variant: "warning" | "success" | "orange" | "secondary";
  label: string;
};

export function hasTowWorkflowUi(citation: Citation): boolean {
  return citation.tow_required === true && citation.tow != null && citation.tow.required === true;
}

export function getTowWorkflowStatus(citation: Citation): TowWorkflowStatus | null {
  if (!hasTowWorkflowUi(citation)) return null;
  const tow = citation.tow;
  if (!tow) return null;

  if (tow.cancelled || tow.status === "cancelled") return "cancelled";
  if (tow.completed || tow.status === "completed") return "completed";

  if (tow.status === "pending") return "pending";
  if (tow.status === "awaiting_request") return "awaiting_request";

  // Legacy/inconsistent payloads (`none` or missing status) stay requestable.
  return "awaiting_request";
}

export function getTowActionMatrix(citation: Citation): TowActionMatrix {
  const status = getTowWorkflowStatus(citation);
  if (!status) {
    return {
      showWorkflow: false,
      canRequestTow: false,
      canCompleteTow: false,
      canCancelTow: false,
      isTerminal: false,
    };
  }

  if (status === "completed" || status === "cancelled") {
    return {
      showWorkflow: true,
      canRequestTow: false,
      canCompleteTow: false,
      canCancelTow: false,
      isTerminal: true,
    };
  }

  if (status === "pending") {
    return {
      showWorkflow: true,
      canRequestTow: false,
      canCompleteTow: true,
      canCancelTow: true,
      isTerminal: false,
    };
  }

  return {
    showWorkflow: true,
    canRequestTow: true,
    canCompleteTow: false,
    canCancelTow: true,
    isTerminal: false,
  };
}

export function getTowBadgeState(citation: Citation): TowBadgeState {
  const status = getTowWorkflowStatus(citation);
  if (!status) {
    return {
      showWorkflow: false,
      variant: "secondary",
      label: "Tow",
    };
  }

  if (status === "cancelled") {
    return {
      showWorkflow: true,
      variant: "orange",
      label: "Tow cancelled",
    };
  }

  if (status === "completed") {
    return {
      showWorkflow: true,
      variant: "success",
      label: "Tow completed",
    };
  }

  if (status === "pending") {
    return {
      showWorkflow: true,
      variant: "warning",
      label: "Tow pending",
    };
  }

  return {
    showWorkflow: true,
    variant: "secondary",
    label: "Tow awaiting request",
  };
}
